import { useContext, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'Components/cypress_ids.json';
import { VEHICLE_PURCHASE_MODAL_TYPES, VehicleInfoContext } from 'Context/VehicleInfoContext';
import { triggerSubmitBidCTAClickedEvent } from 'Services/analytics/events';
import { applyCypressData } from 'Utilities/helpers';

import { useBiddingSection } from '../../useBiddingSection';

import { ProxyBidInputField } from './ProxyBidInputField/ProxyBidInputField';
import { BID_INPUT_ID } from './ProxyBidActions.const';
import LocalTexts from './ProxyBidActions.texts';
import type { BidInputValues } from './ProxyBidActions.types';
import { validateBidInput } from './validateBidInput';

import styles from './ProxyBidActions.module.scss';

const LocalT = new MDText(LocalTexts);

const ProxyBidActionsSidebar: React.FC = () => {
	const { showVehiclePurchaseModal } = useContext(VehicleInfoContext);
	const { minimumBid, priceReserve } = useBiddingSection();

	const validateBidWithMinimumBidValue = useMemo(() => validateBidInput(minimumBid.value), [minimumBid.value]);

	const handlePlaceBidClick = (values: BidInputValues) => {
		const value = values[BID_INPUT_ID];

		const error = validateBidWithMinimumBidValue(value);

		if (error) {
			triggerSubmitBidCTAClickedEvent({ errorMessage: error, success: false });
			return { [BID_INPUT_ID]: error };
		}

		triggerSubmitBidCTAClickedEvent({ bidAmount: value.replaceAll(',', ''), success: true });
		showVehiclePurchaseModal({
			modalType: VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_SIDEBAR,
			value,
		});

		return undefined;
	};

	return (
		<Form
			onSubmit={handlePlaceBidClick}
			render={({ handleSubmit }) => {
				return (
					<form className={styles.desktopContainer} onSubmit={handleSubmit}>
						<Field
							name={BID_INPUT_ID}
							render={({ input, meta }) => <ProxyBidInputField input={input} meta={meta} priceReserve={priceReserve} />}
						/>
					</form>
				);
			}}
		/>
	);
};

const ProxyBidActionsFooter: React.FC = () => {
	const { showVehiclePurchaseModal } = useContext(VehicleInfoContext);

	const onClick = () => {
		triggerSubmitBidCTAClickedEvent({ success: true });
		showVehiclePurchaseModal({
			modalType: VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_FOOTER,
		});
	};

	return (
		<div className={styles.mobileButtonContainer}>
			<Button
				fullWidth={true}
				label={`${LocalT.translate('bidButton.mobile')}`}
				onClick={onClick}
				variant="primary"
				{...applyCypressData(cypressIds.buttons.submitBidFromFooter)}
			/>
		</div>
	);
};

export const ProxyBidActions: React.FC = () => {
	return (
		<div className={styles.container}>
			<div className={styles.hideOnMobileWrapper}>
				<ProxyBidActionsSidebar />
			</div>
			<div className={styles.showOnlyOnMobileWrapper}>
				<ProxyBidActionsFooter />
			</div>
		</div>
	);
};
